import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {withRouter} from 'react-router-dom';
import './styles.css';
import AVATAR_TYPE from '../../../../constants/avatar-type';
import ENDPOINT from '../../../../constants/endpoints';
import {GLOBAL_SEARCH_SECTION} from '../../../../constants/global-search';
import ROUTE from '../../../../constants/route';
import {historySetGridInitialParams} from '../../../../helpers/grid-history';
import LinkBuilder from '../../../../helpers/link-builder';
import {Role} from '../../../../helpers/user';
import useApi from '../../../../hooks/useApi';
import {useReviewAction} from '../../../../hooks/useReviewAction';
import BasicDialog from '../../../Dialog/components/BasicDialog';
import ResponseErrorDialog from '../../../Dialog/components/ResponseErrorDialog';
import GlobalSearchAutosuggest from './components/GlobalSearchAutosuggest';
import GlobalSearchFilterSelect from './components/GlobalSearchFilterSelect';

const GlobalSearch = props => {
  const {
    user,
    history,
    location: { pathname },
  } = props;

  const api = useApi();
  const { isAllowedView: isAllowedViewReview } = useReviewAction(user);

  const sections = [
    {
      label: 'All',
      value: GLOBAL_SEARCH_SECTION.ALL,
      placeholder: 'Search for Jobs, Contacts and Firms',
    },
    {
      label: 'GoBuyside Network',
      value: GLOBAL_SEARCH_SECTION.NETWORK,
      placeholder: 'Search for Contacts',
    },
    {
      label: 'My Network',
      value: GLOBAL_SEARCH_SECTION.MY_NETWORK,
      placeholder: 'Search for Connections',
    },
    {
      label: 'Firms',
      value: GLOBAL_SEARCH_SECTION.FIRM,
      placeholder: 'Search for Firms',
    },
    {
      label: 'Reviews',
      value: GLOBAL_SEARCH_SECTION.REVIEW,
      placeholder: 'Search for Reviews',
    },
    {
      label: 'Jobs',
      value: GLOBAL_SEARCH_SECTION.JOB,
      placeholder: 'Search for Jobs',
    },
    {
      label: 'Inbox',
      value: GLOBAL_SEARCH_SECTION.INBOX,
      placeholder: 'Search for Messages',
    },
  ];

  const [query, setQuery] = useState('');
  const [activeSection, setActiveSection] = useState(sections[0]);

  useEffect(() => {
    setQuery('');
  }, [pathname]);

  const sectionOptions = useMemo(
    () =>
      sections.filter(
        item =>
          !(
            (item.value === GLOBAL_SEARCH_SECTION.MY_NETWORK ||
              item.value === GLOBAL_SEARCH_SECTION.JOB) &&
            !Role.isCandidate(user)
          )
      ),
    [user]
  );

  const handleQueryChange = useCallback((field, value) => {
    setQuery(value);
  }, [setQuery]);

  let errorIsDisplayed = false;

  const onCloseErrorDialog = useCallback(() => {
    errorIsDisplayed = false;
  }, []);

  const handleSearchRequest = useCallback(value => {
    return new Promise(resolve =>
      api.get(
        ENDPOINT.OTHER.GLOBAL_SEARCH_AUTOSUGGEST,
        {search: value},
        {
          handleError: (response) => {
            if (!errorIsDisplayed) {
              errorIsDisplayed = true;
              ResponseErrorDialog({response, controlFunc: onCloseErrorDialog})
            }
          },
          handleSuccess: records =>
            resolve(
              records.map(record => ({
                ...record,
                items: record.items.map(item => ({
                  ...item,
                  avatar:
                    GLOBAL_SEARCH_SECTION.NETWORK === record.group ||
                    GLOBAL_SEARCH_SECTION.MY_NETWORK === record.group
                      ? LinkBuilder.getCandidatePictureSizeLink(
                        item.image,
                        '40x40'
                      )
                      : [AVATAR_TYPE.FIRM, AVATAR_TYPE.JOB].indexOf(
                        item.image
                      ) === -1
                        ? item.image
                        : null,
                })),
              }))
            ),
        }
      )
    );
  }, [])

  const firmItemSelected = useCallback(item => {
    history.push(LinkBuilder.getFirmLink(item.FirmUrlAlias));
  }, [history]);

  const networkItemSelected = useCallback(item => {
    item.ViewProfile === 'Yes' && item.id
      ? history.push(LinkBuilder.getProfileLink(item.id))
      : history.push(ROUTE.NETWORK, { initialParams: { search: query } });
  }, [history, query]);

  const myNetworkItemSelected = useCallback(item => {
    item.ViewProfile === 'Yes' && item.id
      ? history.push(LinkBuilder.getProfileLink(item.id))
      : history.push(ROUTE.MY_NETWORK, { initialParams: { search: query } });
  }, [history, query]);

  const reviewItemSelected = useCallback(item => {
    if (isAllowedViewReview(item)) {
      history.push(
        LinkBuilder.getReviewLink(
          item.ReviewType,
          item.FirmUrlAlias,
          item.PublicName
        )
      );
    }
  }, [history, isAllowedViewReview]);

  const jobItemSelected = useCallback(item => {
    history.push(LinkBuilder.getJobLink(item.id));
  }, [history]);

  const inboxItemSelected = useCallback(item => {
    history.push(ROUTE.INBOX, { message: item.id });
  }, [history]);

  const handleSelected = useCallback((value, section) => {
    if (GLOBAL_SEARCH_SECTION.FIRM === section) {
      firmItemSelected(value);
    } else if (GLOBAL_SEARCH_SECTION.REVIEW === section) {
      reviewItemSelected(value);
    } else if (Role.isCandidate(user, true)) {
      if (GLOBAL_SEARCH_SECTION.NETWORK === section) {
        networkItemSelected(value);
      } else if (GLOBAL_SEARCH_SECTION.MY_NETWORK === section) {
        myNetworkItemSelected(value);
      } else if (GLOBAL_SEARCH_SECTION.JOB === section) {
        jobItemSelected(value);
      } else if (GLOBAL_SEARCH_SECTION.INBOX === section) {
        inboxItemSelected(value);
      }
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (query.length > 500) {
      BasicDialog({
        bodyRender: <p>Value is too long. Maximum 500 symbols allowed</p>,
      });
      return;
    }

    if (query.length) {
      setQuery('');
      if (
        GLOBAL_SEARCH_SECTION.REVIEW === activeSection.value &&
        !Role.isGuest(user)
      ) {
        historySetGridInitialParams(history, ROUTE.REVIEWS, { search: query });
      } else if (
        Role.isCandidate(user) &&
        GLOBAL_SEARCH_SECTION.FIRM !== activeSection.value &&
        GLOBAL_SEARCH_SECTION.ALL !== activeSection.value
      ) {
        if (GLOBAL_SEARCH_SECTION.NETWORK === activeSection.value) {
          historySetGridInitialParams(history, ROUTE.NETWORK, {
            search: query,
          });
        } else if (GLOBAL_SEARCH_SECTION.MY_NETWORK === activeSection.value) {
          historySetGridInitialParams(history, ROUTE.MY_NETWORK, {
            search: query,
          });
        } else if (GLOBAL_SEARCH_SECTION.JOB === activeSection.value) {
          historySetGridInitialParams(history, ROUTE.JOBS, { search: query });
        } else if (GLOBAL_SEARCH_SECTION.INBOX === activeSection.value) {
          historySetGridInitialParams(history, ROUTE.INBOX, { search: query });
        }
      } else {
        historySetGridInitialParams(history, ROUTE.GLOBAL_SEARCH, {
          search: query,
          section: activeSection.value,
        });
      }
    }
  }, [query, activeSection, user]);

  return (
    <div className="global-search">
      <div className="global-search__form">
        <div className="global-search__select">
          <GlobalSearchFilterSelect
            name="filter"
            value={activeSection}
            isValid={true}
            options={sectionOptions}
            onChange={(name, value) => setActiveSection(value)}
          />
        </div>
        <div className="global-search__input">
          <GlobalSearchAutosuggest
            name="search"
            value={query}
            placeholder={
              activeSection
                ? activeSection.placeholder
                : sectionOptions[0].placeholder
            }
            onEnterPress={handleSubmit}
            onChange={handleQueryChange}
            onSearchRequested={handleSearchRequest}
            onSelected={handleSelected}
          />
          <button
            type="button"
            className="global-search__btn"
            onClick={handleSubmit}
          >
            <div className="global-search__btn-icon">
              <svg
                width="22"
                height="22"
                fill="none"
                viewBox={'0 0 16 16'}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7 2.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9ZM1.5 7a5.5 5.5 0 1 1 9.73 3.52l3.12 3.13a.5.5 0 0 1-.7.7l-3.13-3.12A5.5 5.5 0 0 1 1.5 7Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default withRouter(GlobalSearch);
